// @ts-check
// Rotas REST dos fluxos de automação (spec 2.0.0, fluxos.md seção 18.1; contrato 09 de fluxos, seção 16). Todas exigem
// a flag WORKFLOWS, menos a seção do card (só a leitura do card). No modo demonstração, client/src/data/mock-flows.js
// responde no mesmo formato.
import { api, requestFile } from './api-client.js';

/**
 * @typedef {import('../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../lib/flow-list.js').FlowSummary} FlowSummary
 * @typedef {{ id: string, name: string, description: string | null, collection: string | null, status: 'DRAFT' | 'ACTIVE' | 'INACTIVE',
 *   inactiveReason: string | null, inactiveDetails?: Record<string, unknown> | null, revision: number, definition: FlowDefinition,
 *   owner: { id: string, name: string } | null, templateKey: string | null, createdAt: string, updatedAt: string, activatedAt: string | null,
 *   firstActivatedAt: string | null, deactivatedAt?: string | null, problems: FlowProblem[], hasRuns: boolean }} FlowDetail
 * @typedef {{ key: string, kind: 'EMAIL' | 'WHATSAPP', nodeKey: string, labelKey: string, emailGalleryKey?: string,
 *   whatsappHint?: { category: 'MARKETING' | 'UTILITY', bodyExample: string } }} FlowTemplateChoice
 * @typedef {{ key: string, nameKey: string, descriptionKey: string, category: string, icon: string, channels: Array<'EMAIL' | 'WHATSAPP'>,
 *   choices: FlowTemplateChoice[], definition: FlowDefinition }} FlowTemplateItem
 * @typedef {{ removedNodeIds: string[], remap: Record<string, string | null>, affectedRuns: number, changedUntilDateNodeIds: string[],
 *   typeChangedNodeIds?: string[], remapCounts?: Record<string, number>, entryChanged?: boolean, retroactiveCount?: number | null }} FlowEditPlanDetails
 * @typedef {{ isReady: boolean, problems: number, [key: string]: unknown }} FlowChecklistRow
 * @typedef {{ checklist: { entry: FlowChecklistRow, actions: FlowChecklistRow & { nodes?: number, considerWeekends?: boolean },
 *   exit: FlowChecklistRow & { criteria?: string[] }, sends: FlowChecklistRow }, problems: FlowProblem[],
 *   retroactive: { count: number, isEstimate: boolean, warning: 'LARGE_ENTRY' | 'TOO_LARGE' | null } | null,
 *   sendEstimates: { emails: { nodes: number, marketing: number, service: number, firstStretch: number },
 *     whatsapp: { nodes: number, marketing: number, utility: number, firstStretch: number } },
 *   warnings: Array<{ code: string, details?: Record<string, unknown> }>, consentMode: string | null }} FlowActivationPreview
 * @typedef {{ now: number, failedNow: number, arrived: number, passed: number, skipped: number, failed: number, exited: number,
 *   branches: Record<string, number> | null, sent?: number, delivered?: number, read?: number, opened?: number, clicked?: number }} FlowNodeCount
 * @typedef {{ computedAt: string, entry: { entered: number, inFlow: number, completed: number, exited: number, stopped: number },
 *   nodes: Record<string, FlowNodeCount> }} FlowNodeCounts
 * @typedef {{ id: string, kind: string, status: string, totalCards: number | null, processed: number, inserted: number, skipped: number,
 *   skippedByReason?: Record<string, number>, error: string | null }} FlowJob
 * @typedef {{ id: string, cardId: string, cardLabel: string, status: string, currentNode: { id: string, type: string, title: string | null } | null,
 *   enteredAt: string, endedAt: string | null, resumeAt: string | null, waitKind: string | null, exitReason: string | null, entrySource: string | null,
 *   error: string | null }} FlowRunSummary
 * @typedef {{ id: string, flowId: string, flowName: string, status: string, currentNode: { id: string, type: string, title: string | null } | null,
 *   enteredAt: string, endedAt: string | null, exitReason: string | null, resumeAt: string | null, waitKind?: string | null }} RecordFlowRun
 */

/** @param {any} body */
const dataOf = (body) => body?.data ?? {};
/** @param {string} flowId */
const flowPath = (flowId) => `/rest/flows/${encodeURIComponent(flowId)}`;

/**
 * @param {Record<string, string | null | undefined>} [query] busca, situação, coleção, canal, ordem
 * @returns {Promise<{ flows: FlowSummary[], collections: string[] }>}
 */
export async function listFlows(query = {}) {
  const data = dataOf(await api.get('/rest/flows', query));
  return { flows: data.flows ?? [], collections: data.collections ?? [] };
}

/**
 * Fluxo novo: em branco, por modelo (com as escolhas das mensagens) ou cópia de outro com a definição de quem pede.
 * Com `duplicateOf` e `definition`, a cópia leva a definição enviada numa chamada só (o servidor dá ids novos aos nós e
 * mantém os segredos guardados do fluxo de origem).
 * @param {{ name: string, collection?: string | null, description?: string | null, templateKey?: string, choices?: Record<string, any>,
 *   duplicateOf?: string, definition?: unknown }} input
 * @returns {Promise<FlowDetail>}
 */
export async function createFlow(input) {
  return dataOf(await api.post('/rest/flows', input)).flow;
}

/** @returns {Promise<FlowTemplateItem[]>} */
export async function listFlowTemplates() {
  return dataOf(await api.get('/rest/flows/templates')).templates ?? [];
}

/** @param {string} flowId @returns {Promise<FlowDetail>} */
export async function getFlow(flowId) {
  return dataOf(await api.get(flowPath(flowId))).flow;
}

/**
 * Grava a definição. Fluxo ativo com impacto pede `confirmImpact`; outra pessoa salvou antes: 409 EDIT_CONFLICT.
 * @param {string} flowId @param {{ definition: FlowDefinition, expectedRevision: number, confirmImpact?: boolean, primeChangedEntry?: boolean }} input
 * @returns {Promise<{ flow: FlowDetail, plan: FlowEditPlanDetails | null }>}
 */
export async function saveFlowDefinition(flowId, input) {
  const data = dataOf(await api.put(flowPath(flowId), input));
  return { flow: data.flow, plan: data.plan ?? null };
}

/** Nome, descrição e coleção. @param {string} flowId @param {{ name?: string, description?: string | null, collection?: string | null }} patch */
export async function updateFlowDetails(flowId, patch) {
  return /** @type {FlowDetail} */ (dataOf(await api.patch(flowPath(flowId), patch)).flow);
}

/** @param {string} flowId */
export async function deleteFlow(flowId) {
  return dataOf(await api.delete(flowPath(flowId)));
}

/** @param {string} flowId @param {{ name?: string }} [input] @returns {Promise<FlowDetail>} */
export async function duplicateFlow(flowId, input = {}) {
  return dataOf(await api.post(`${flowPath(flowId)}/duplicate`, input)).flow;
}

/**
 * Prévia do modal de ativação (sem definição: a gravada).
 * @param {string} flowId @param {{ expectedRevision?: number, definition?: FlowDefinition }} input
 * @returns {Promise<FlowActivationPreview>}
 */
export async function previewFlowActivation(flowId, input) {
  return dataOf(await api.post(`${flowPath(flowId)}/activation-preview`, input));
}

/**
 * Ativa; entrada grande precisa de `acknowledgeLargeEntry` (409 ACKNOWLEDGEMENT_REQUIRED sem ela).
 * @param {string} flowId @param {{ expectedRevision: number, acknowledgeLargeEntry?: boolean }} input
 * @returns {Promise<{ flow: FlowDetail, jobs: Array<{ id: string, kind: string }> }>}
 */
export async function activateFlow(flowId, input) {
  const data = dataOf(await api.post(`${flowPath(flowId)}/activate`, input));
  return { flow: data.flow, jobs: data.jobs ?? [] };
}

/** @param {string} flowId @param {'STOP_RUNS' | 'HOLD_RUNS'} mode @returns {Promise<{ flow: FlowDetail, affectedRuns: number }>} */
export async function deactivateFlow(flowId, mode) {
  const data = dataOf(await api.post(`${flowPath(flowId)}/deactivate`, { mode }));
  return { flow: data.flow, affectedRuns: Number(data.affectedRuns ?? 0) };
}

/**
 * Inserção manual: por ids (até 200, resposta na hora) ou por público (tarefa em segundo plano).
 * @param {string} flowId @param {{ recordIds?: string[], audience?: Record<string, any>, allowReentry?: boolean }} input
 * @returns {Promise<{ inserted?: Array<{ recordId: string, runId: string }>, skipped?: Array<{ recordId: string, reason: string }>, job?: FlowJob }>}
 */
export async function insertLeadsIntoFlow(flowId, input) {
  return dataOf(await api.post(`${flowPath(flowId)}/insert`, input));
}

/** @param {string} flowId @param {string} jobId @returns {Promise<FlowJob>} */
export async function getFlowJob(flowId, jobId) {
  return dataOf(await api.get(`${flowPath(flowId)}/jobs/${encodeURIComponent(jobId)}`)).job;
}

/** @param {string} flowId @param {string[]} recordIds @returns {Promise<{ removed: number, notInFlow: number }>} */
export async function removeLeadsFromFlow(flowId, recordIds) {
  const data = dataOf(await api.post(`${flowPath(flowId)}/remove`, { recordIds }));
  return { removed: Number(data.removed ?? 0), notInFlow: Number(data.notInFlow ?? 0) };
}

/** @param {string} flowId @returns {Promise<FlowNodeCounts>} */
export async function getFlowNodeCounts(flowId) {
  return dataOf(await api.get(`${flowPath(flowId)}/node-counts`));
}

/** @param {string} flowId @param {{ from: string, to: string, groupBy: string }} period */
export async function getFlowStats(flowId, period) {
  return dataOf(await api.get(`${flowPath(flowId)}/stats`, period));
}

/**
 * Cards de um contador (lista pelo clique no gráfico, sempre por fotografia).
 * @param {string} flowId @param {string} nodeKey id do nó ou 'entry' @param {{ metric: string, from?: string, to?: string }} input
 * @returns {Promise<{ url: string, count: number, drilldownParam: string }>}
 */
export async function resolveFlowNodeDrilldown(flowId, nodeKey, input) {
  return dataOf(await api.post(`${flowPath(flowId)}/nodes/${encodeURIComponent(nodeKey)}/drilldown`, input));
}

/**
 * @param {string} flowId @param {{ status?: string, nodeId?: string, cardId?: string, limit?: number, startingAfter?: string | null }} [query]
 * @returns {Promise<{ runs: FlowRunSummary[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listFlowRuns(flowId, query = {}) {
  const data = dataOf(await api.get(`${flowPath(flowId)}/runs`, query));
  return { runs: data.runs ?? [], pageInfo: data.pageInfo ?? { hasNextPage: false, endCursor: null } };
}

/** @param {string} runId */
export async function getFlowRun(runId) {
  return dataOf(await api.get(`/rest/flows/runs/${encodeURIComponent(runId)}`));
}

/** @param {string} flowId @param {{ runIds?: string[], nodeId?: string }} input @returns {Promise<number>} */
export async function retryFlowRuns(flowId, input) {
  return Number(dataOf(await api.post(`${flowPath(flowId)}/runs/retry`, input)).retried ?? 0);
}

/** @param {string} runId */
export async function stopFlowRun(runId) {
  return dataOf(await api.post(`/rest/flows/runs/${encodeURIComponent(runId)}/stop`, {})).run;
}

/** @param {string} flowId */
export async function listFlowRevisions(flowId) {
  return dataOf(await api.get(`${flowPath(flowId)}/revisions`)).revisions ?? [];
}

/** Relatório em planilha dos fluxos no período. @param {{ from?: string, to?: string }} [query] */
export async function downloadFlowsReport(query = {}) {
  return requestFile('/rest/flows/report.csv', query);
}

/** Seção "Fluxos de automação" do card. @param {string} cardId @returns {Promise<{ runs: RecordFlowRun[], canManage: boolean }>} */
export async function listFlowsForRecord(cardId) {
  const data = dataOf(await api.get(`/rest/flows/for-record/${encodeURIComponent(cardId)}`));
  return { runs: data.runs ?? [], canManage: data.canManage === true };
}
